import { Box, Button, Divider, Flex, FormLabel, Heading, Collapse, Text, Tooltip, IconButton } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IconInfoCircle } from 'assets/icons';
import { FormikSelect, FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { hourOptions, timeOptions } from 'containers/CompanySettings/shared/options';
import FEATURES from 'global/features';
import { PLANS } from 'global/plans';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import {
  useSaveNotificationSettings,
  ISaveNotificationSettings,
} from 'hooks/companySettings/useSaveNotificationSettings';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';

import { initialNotificationSettingsData, notificationSettingsValidation } from './schema';

const General: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [formData, setFormData] = useState<any>(null);

  const companyInfo = useCompanyInfo();
  const featureList = useFeatureList();

  const { data, status, refetch } = useFetchSettings('notification');
  const { status: saveNotificationStatus, mutateAsync } = useSaveNotificationSettings();

  useEffect(
    () => {
      if (status === 'success') {
        const pFormData: ISaveNotificationSettings = {
          ...data?.payload,
          overtime_threshold: !isNaN(data?.payload?.overtime_threshold)
            ? data?.payload?.overtime_threshold === 0
              ? hourOptions[0]
              : hourOptions.find((item: any) => item.value === data?.payload?.overtime_threshold)
            : null,
          clockout_threshold: !isNaN(data?.payload?.clockout_threshold)
            ? data?.payload?.clockout_threshold === 0
              ? hourOptions[0]
              : hourOptions.find((item: any) => item.value === data?.payload?.clockout_threshold)
            : null,
          clock_in_reminder_time: data?.payload?.clock_in_reminder?.time
            ? timeOptions.find((item: any) => item.value === data?.payload?.clock_in_reminder?.time)
            : null,
          clock_in_reminder_reminder_mobile: data?.payload?.clock_in_reminder?.reminder_mobile,
          clock_in_reminder_reminder_email: data?.payload?.clock_in_reminder?.reminder_email,
          clock_out_reminder_time: data?.payload?.clock_out_reminder?.time
            ? timeOptions.find((item: any) => item.value === data?.payload?.clock_out_reminder?.time)
            : null,
          clock_out_reminder_reminder_mobile: data?.payload?.clock_out_reminder?.reminder_mobile,
          clock_out_reminder_reminder_email: data?.payload?.clock_out_reminder?.reminder_email,
          break_reminder_after_x_hours: data?.payload?.break_reminder_after_x_hours
            ? timeOptions.find((item: any) => item.value === data?.payload?.break_reminder_after_x_hours)
            : null,
          cali_send_meal_break_reminder_after_hrs: !isNaN(data?.payload?.cali_send_meal_break_reminder_after_hrs)
            ? data?.payload?.cali_send_meal_break_reminder_after_hrs
              ? hourOptions.find((item: any) => item.value === data?.payload?.cali_send_meal_break_reminder_after_hrs)
              : hourOptions[0]
            : null,
          cali_send_rest_break_reminder_after_hrs: !isNaN(data?.payload?.cali_send_rest_break_reminder_after_hrs)
            ? data?.payload?.cali_send_rest_break_reminder_after_hrs
              ? hourOptions.find((item: any) => item.value === data?.payload?.cali_send_rest_break_reminder_after_hrs)
              : hourOptions[0]
            : null,
        };
        setFormData(pFormData);
      }
    },
    // eslint-disable-next-line
    [status]
  );

  const handleToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    setFieldValue(controlName, e.target.checked);
  };

  const handleFormSubmit = async (values: typeof initialNotificationSettingsData) => {
    const postData: ISaveNotificationSettings = {
      employee_clock_out_of_bounds: values.employee_clock_out_of_bounds,
      schedule_notification_by_email: values.schedule_notification_by_email,
      schedule_notification_by_mobile: values.schedule_notification_by_mobile,
      overtime_notification: values.overtime_notification,
      automatic_clockout: values.automatic_clockout,
      send_reminder_to_take_breaks: values.send_reminder_to_take_breaks,
      break_reminder_after_x_hours: values.send_reminder_to_take_breaks ? values.break_reminder_after_x_hours : 0,
      send_reminder_for_no_show: values.send_reminder_for_no_show,
      send_reminder_for_late_clock_in: values.send_reminder_for_late_clock_in,
      overtime_threshold: values.overtime_notification ? values.overtime_threshold.value : 0,
      clockout_threshold: values.automatic_clockout ? values.clockout_threshold.value : 0,
      clock_in_reminder: {
        time: values?.clock_in_reminder_time.value,
        reminder_mobile: values?.clock_in_reminder_reminder_mobile,
        reminder_email: values?.clock_in_reminder_reminder_email,
      },
      clock_out_reminder: {
        time: values?.clock_out_reminder_time.value,
        reminder_mobile: values?.clock_out_reminder_reminder_mobile,
        reminder_email: values?.clock_out_reminder_reminder_email,
      },
      cali_send_rest_break_reminder_after_hrs: values.cali_send_rest_break_reminder_after_hrs?.value
        ? values.cali_send_rest_break_reminder_after_hrs?.value
        : null,
      cali_send_meal_break_reminder_after_hrs: values.cali_send_meal_break_reminder_after_hrs?.value
        ? values.cali_send_meal_break_reminder_after_hrs?.value
        : null,
    };

    mutateAsync(postData).then(() => {
      refetch();
    });
  };

  if (status === 'loading' || !formData) return <LogoLoader />;

  return (
    <Box mt="26px">
      <Formik
        initialValues={formData}
        validationSchema={notificationSettingsValidation}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form noValidate>
            <Box display="flex" flexDirection="column" maxWidth="587px">
              {!(companyInfo?.plan.toLowerCase() === PLANS.basic) && (
                <React.Fragment>
                  <Box display="flex" justifyContent="space-between" mb="17px">
                    <FormLabel fontSize="sm">
                      {t('companySettings:notifyAdminIfUserClocksOutOfBoundsViaMobilePushNotification')}
                    </FormLabel>
                    <FormikSwitch
                      name="employee_clock_out_of_bounds"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'employee_clock_out_of_bounds')}
                    ></FormikSwitch>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb="17px">
                    <FormLabel fontSize="sm">
                      {t('companySettings:notifyAllUsersOfaNewOrUpdatedShiftViaEmail')}
                    </FormLabel>
                    <FormikSwitch
                      name="schedule_notification_by_email"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'schedule_notification_by_email')}
                    ></FormikSwitch>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb="17px">
                    <FormLabel fontSize="sm">
                      {t('companySettings:notifyAllUsersOfNewOrUpdatedShiftViaMobilePushNotification')}
                    </FormLabel>
                    <FormikSwitch
                      name="schedule_notification_by_mobile"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'schedule_notification_by_mobile')}
                    ></FormikSwitch>
                  </Box>
                </React.Fragment>
              )}

              <Box display="flex" justifyContent="space-between" mb="17px">
                <FormLabel fontSize="sm">
                  {t('companySettings:notifyWhenUserGoesOvertimeViaEmailAndMobilePushNotification')}
                </FormLabel>
                <FormikSwitch
                  name="overtime_notification"
                  onChangeCallback={e => handleToggle(e, setFieldValue, 'overtime_notification')}
                ></FormikSwitch>
              </Box>
              <Collapse in={values.overtime_notification} unmountOnExit={true} animateOpacity>
                <Box maxWidth="390px">
                  <FormikSelect
                    name="overtime_threshold"
                    label={t('companySettings:overtimeThresholdInHrs')}
                    options={hourOptions}
                    selectProps={{
                      placeholder: t('companySettings:selectHours'),
                      isClearable: false,
                    }}
                  />
                </Box>
              </Collapse>
              <Box display="flex" justifyContent="space-between" mb="17px">
                <FormLabel fontSize="sm">
                  {t('companySettings:clockOutAUserAfterTheyExceedACertainNumberOfHours')}
                </FormLabel>
                <FormikSwitch
                  name="automatic_clockout"
                  onChangeCallback={e => handleToggle(e, setFieldValue, 'automatic_clockout')}
                ></FormikSwitch>
              </Box>
              <Collapse in={values.automatic_clockout} unmountOnExit={true} animateOpacity>
                <Box maxWidth="390px">
                  <FormikSelect
                    name="clockout_threshold"
                    label={t('companySettings:clockOutAfterInHrs')}
                    options={hourOptions}
                    selectProps={{
                      placeholder: t('companySettings:selectHours'),
                      isClearable: false,
                    }}
                  />
                </Box>
              </Collapse>

              {/* <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:notifyAdminOfNoShows')}</FormLabel>
                  <FormikSwitch
                    name="send_reminder_for_no_show"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'send_reminder_for_no_show')}
                  ></FormikSwitch>
                </Box> */}
              <Box
                display="flex"
                justifyContent="space-between"
                mb="17px"
                cursor={
                  companyInfo?.plan?.toLowerCase() === PLANS.premium ||
                  companyInfo?.plan?.toLowerCase() === PLANS.pro ||
                  featureList[FEATURES.scheduling]
                    ? 'auto'
                    : 'not-allowed'
                }
                color={
                  companyInfo?.plan?.toLowerCase() === PLANS.premium ||
                  companyInfo?.plan?.toLowerCase() === PLANS.pro ||
                  featureList[FEATURES.scheduling]
                    ? 'inherit'
                    : 'grey'
                }
              >
                <FormLabel
                  fontSize="sm"
                  cursor={
                    companyInfo?.plan?.toLowerCase() === PLANS.premium ||
                    companyInfo?.plan?.toLowerCase() === PLANS.pro ||
                    featureList[FEATURES.scheduling]
                      ? 'auto'
                      : 'not-allowed'
                  }
                >
                  <Flex alignItems="center">
                    {t('companySettings:notifyAdminAboutLateClockinsViaEmail')}
                    {!(
                      companyInfo?.plan?.toLowerCase() === PLANS.premium ||
                      companyInfo?.plan?.toLowerCase() === PLANS.pro ||
                      featureList[FEATURES.scheduling]
                    ) && (
                      <Text ml={2} fontSize="xs" color="secondary.700">
                        ({t('common:pro')} {t('common:plan')})
                      </Text>
                    )}
                    <Tooltip
                      bg="black"
                      label={t('companySettings:managersWillReceiveEmailForEmployeesWithinTheirGroupOnly')}
                      hasArrow
                    >
                      <Box cursor="pointer">
                        <IconButton
                          as={IconInfoCircle}
                          aria-label="add-ons information"
                          variant="unstyled"
                          w="16px"
                          h="16px"
                          sx={{
                            path: { fill: 'primary.500' },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                </FormLabel>
                <FormikSwitch
                  name="send_reminder_for_late_clock_in"
                  onChangeCallback={e => handleToggle(e, setFieldValue, 'send_reminder_for_late_clock_in')}
                  switchProps={{
                    isDisabled: !(
                      companyInfo?.plan?.toLowerCase() === PLANS.premium ||
                      companyInfo?.plan?.toLowerCase() === PLANS.pro ||
                      featureList[FEATURES.scheduling]
                    ),
                  }}
                ></FormikSwitch>
              </Box>

              {/* Send reminder to take breaks */}
              {/* <Box display="flex" justifyContent="space-between" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:sendReminderToTakeBreaks')}</FormLabel>
                  <FormikSwitch
                    name="send_reminder_to_take_breaks"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'send_reminder_to_take_breaks')}
                  ></FormikSwitch>
                </Box>
                <Collapse in={values.send_reminder_to_take_breaks} unmountOnExit={true} animateOpacity>
                  <Box maxWidth="390px">
                    <FormikSelect
                      name="break_reminder_after_x_hours"
                      label={t('companySettings:remindUsersToTakeBreakAfterInHrs')}
                      options={breakHourOptions}
                      selectProps={{
                        placeholder: t('companySettings:selectHours'),
                        isClearable: false,
                      }}
                    />
                  </Box>
                </Collapse> */}
            </Box>
            <Divider orientation="horizontal" variant="dashed" mb="57px" />
            {(companyInfo?.plan?.toLowerCase() === PLANS.premium ||
              companyInfo?.plan?.toLowerCase() === PLANS.pro ||
              featureList[FEATURES.scheduling]) && (
              <>
                <Flex alignItems="center" mb="6" flexWrap="wrap">
                  <FormLabel fontSize="sm" mb="0">
                    {t('companySettings:sendReminderToClockIn')}:
                  </FormLabel>
                  <FormikSelect
                    name="clock_in_reminder_time"
                    options={timeOptions}
                    formControlProps={{ mb: 0, width: '120px', mr: '16' }}
                    selectProps={{
                      placeholder: t('companySettings:selectTime'),
                      isClearable: false,
                    }}
                  />
                  <Flex alignItems="center" mr={16} my="3">
                    <FormLabel fontSize="sm" color="#000000" mr={5}>
                      {t('common:mobile')}
                    </FormLabel>
                    <FormikSwitch
                      name="clock_in_reminder_reminder_mobile"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_in_reminder_reminder_mobile')}
                    ></FormikSwitch>
                  </Flex>
                  <Flex alignItems="center" my="3">
                    <FormLabel fontSize="sm" color="#000000" mr={5}>
                      {t('common:email')}
                    </FormLabel>
                    <FormikSwitch
                      name="clock_in_reminder_reminder_email"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_in_reminder_reminder_email')}
                    ></FormikSwitch>
                  </Flex>
                </Flex>
                <Flex alignItems="center" flexWrap="wrap">
                  <FormLabel fontSize="sm" mb="0">
                    {t('companySettings:sendReminderToClockOut')}:
                  </FormLabel>
                  <FormikSelect
                    name="clock_out_reminder_time"
                    formControlProps={{ mb: 0, width: '120px', mr: '16' }}
                    options={timeOptions}
                    label=""
                    selectProps={{
                      placeholder: t('companySettings:selectTime'),
                      isClearable: false,
                    }}
                  />
                  <Flex alignItems="center" mr={16} my="3">
                    <FormLabel fontSize="sm" color="#000000" mr={5}>
                      {t('common:mobile')}
                    </FormLabel>
                    <FormikSwitch
                      name="clock_out_reminder_reminder_mobile"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_out_reminder_reminder_mobile')}
                    ></FormikSwitch>
                  </Flex>
                  <Flex alignItems="center" my="3">
                    <FormLabel fontSize="sm" color="#000000" mr={5}>
                      {t('common:email')}
                    </FormLabel>
                    <FormikSwitch
                      name="clock_out_reminder_reminder_email"
                      onChangeCallback={e => handleToggle(e, setFieldValue, 'clock_out_reminder_reminder_email')}
                    ></FormikSwitch>
                  </Flex>
                </Flex>
              </>
            )}

            {/* California break reminders section */}
            {companyInfo?.plan.toLowerCase() !== PLANS.basic && companyInfo?.allowManualBreak ? (
              <>
                <Divider orientation="horizontal" variant="dashed" my={3} />
                <Heading as="h3" mr="6" fontWeight="normal" fontSize="xl" mb={{ base: '2', md: '6' }}>
                  {t('companySettings:californiaBreakReminders')}:
                </Heading>

                <Box w="390px">
                  <FormikSelect
                    name="cali_send_meal_break_reminder_after_hrs"
                    label={t('companySettings:sendMealBreakReminderInHours')}
                    options={hourOptions}
                    selectProps={{
                      placeholder: t('companySettings:selectHours'),
                      isClearable: false,
                    }}
                  />
                </Box>
                <Box w="390px">
                  <FormikSelect
                    name="cali_send_rest_break_reminder_after_hrs"
                    label={t('companySettings:sendRestBreakReminderInHours')}
                    options={hourOptions}
                    selectProps={{
                      placeholder: t('companySettings:selectHours'),
                      isClearable: false,
                    }}
                  />
                </Box>
              </>
            ) : null}

            <Button
              mt="15"
              type="submit"
              colorScheme="secondary"
              isLoading={saveNotificationStatus === 'loading'}
              minWidth="100px"
            >
              {t('common:save')}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default General;
